'use client';

/**
 * ConfidenceBadge Component
 *
 * Displays confidence scores for LLM-extracted fields with color-coded indicators.
 *
 * Color scheme:
 * - Green (>=0.8): High confidence
 * - Yellow (0.5-0.8): Medium confidence
 * - Red (<0.5): Low confidence
 */

import CheckCircle from '@mui/icons-material/CheckCircle';
import Error from '@mui/icons-material/Error';
import Warning from '@mui/icons-material/Warning';
import Box from '@mui/material/Box';
import type { SxProps, Theme } from '@mui/material/styles';
import type { TFunction } from '@ops-matrix/shared-types';
import { MatchLevel } from '@ops-matrix/shared-types';
import { useI18n } from '../../hooks/useI18n';
import { GlassChip } from '../GlassChip/GlassChip';
import { GlassTooltip } from '../GlassTooltip/GlassTooltip';

// ============================================================================
// Types
// ============================================================================

export interface ConfidenceBadgeProps {
  /**
   * Confidence score between 0 and 1
   */
  confidence: number;

  /**
   * Size variant
   * @default 'small'
   */
  size?: 'small' | 'medium';

  /**
   * Show the numeric percentage
   * @default false
   */
  showPercentage?: boolean;

  /**
   * Show icon
   * @default true
   */
  showIcon?: boolean;

  /**
   * Custom tooltip content
   */
  tooltipContent?: string;

  /**
   * Additional styles
   */
  sx?: SxProps<Theme>;
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get confidence level from score
 */
function getConfidenceLevel(confidence: number): MatchLevel {
  if (confidence >= 0.8) return MatchLevel.HIGH;
  if (confidence >= 0.5) return MatchLevel.MEDIUM;
  return MatchLevel.LOW;
}

/**
 * Get color for confidence level
 */
function getConfidenceColor(level: MatchLevel): 'success' | 'warning' | 'error' {
  switch (level) {
    case MatchLevel.HIGH:
      return 'success';
    case MatchLevel.MEDIUM:
      return 'warning';
    case MatchLevel.LOW:
    default:
      return 'error';
  }
}

/**
 * Get icon for confidence level
 */
function getConfidenceIcon(level: MatchLevel) {
  switch (level) {
    case MatchLevel.HIGH:
      return CheckCircle;
    case MatchLevel.MEDIUM:
      return Warning;
    case MatchLevel.LOW:
    default:
      return Error;
  }
}

/**
 * Get i18n key for confidence level label
 */
function getConfidenceLabelKey(level: MatchLevel): string {
  switch (level) {
    case MatchLevel.HIGH:
      return 'confidence.high';
    case MatchLevel.MEDIUM:
      return 'confidence.medium';
    case MatchLevel.LOW:
    default:
      return 'confidence.low';
  }
}

/**
 * Get i18n key for confidence level explanation
 */
function getConfidenceExplanationKey(level: MatchLevel): string {
  switch (level) {
    case MatchLevel.HIGH:
      return 'confidence.high.explanation';
    case MatchLevel.MEDIUM:
      return 'confidence.medium.explanation';
    case MatchLevel.LOW:
    default:
      return 'confidence.low.explanation';
  }
}

/**
 * Get default tooltip content with i18n support
 */
function getDefaultTooltip(confidence: number, level: MatchLevel, t: TFunction): string {
  const percentage = Math.round(confidence * 100);
  const levelLabel = t(getConfidenceLabelKey(level));
  const explanation = t(getConfidenceExplanationKey(level));

  return `${levelLabel} (${percentage}%)\n\n${explanation}`;
}

// ============================================================================
// Component
// ============================================================================

export function ConfidenceBadge({
  confidence,
  size = 'small',
  showPercentage = false,
  showIcon = true,
  tooltipContent,
  sx,
}: ConfidenceBadgeProps) {
  const { t } = useI18n();

  // Validate confidence score
  const validConfidence = Math.max(0, Math.min(1, confidence));
  const level = getConfidenceLevel(validConfidence);
  const color = getConfidenceColor(level);
  const Icon = getConfidenceIcon(level);
  const percentage = Math.round(validConfidence * 100);

  // Build label
  let label = t(getConfidenceLabelKey(level));
  if (showPercentage) {
    label = `${percentage}%`;
  }

  // Build tooltip
  const tooltip = tooltipContent || getDefaultTooltip(validConfidence, level, t);

  return (
    <GlassTooltip title={<Box sx={{ whiteSpace: 'pre-line', textAlign: 'left' }}>{tooltip}</Box>} arrow placement="top">
      <Box sx={{ display: 'inline-flex', ...sx }}>
        <GlassChip
          label={label}
          color={color}
          size={size}
          icon={showIcon ? <Icon /> : undefined}
          variant="filled"
          glowOnHover
          hoverScale
        />
      </Box>
    </GlassTooltip>
  );
}

export default ConfidenceBadge;
